import { Injectable, signal, inject } from '@angular/core';
import { ConsentApiService } from '../shared/services/api/consent-api.service';
import { SelectedWorkspaceStore } from '../stores/selected-workspace.store';
import { catchError, of, Subject, tap } from 'rxjs';
import {
  CookieConsent,
  COOKIE_CONSENT_STORAGE_KEY,
  COOKIE_CONSENT_VERSION,
  readStoredCookieConsent,
} from './cookie-consent.storage';

export type { CookieConsent };

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private consentApi = inject(ConsentApiService);
  private workspaceStore = inject(SelectedWorkspaceStore);

  /** null = user has not saved a choice in the banner yet */
  consent = signal<CookieConsent | null>(null);
  forceShow = signal(false);
  readonly consentChanged$ = new Subject<CookieConsent>();

  constructor() {
    this.load();
  }

  private load() {
    const parsed = readStoredCookieConsent();
    if (parsed) {
      this.consent.set(parsed);
    }
  }

  private save(consent: CookieConsent, emitChange = true) {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(consent));
    this.consent.set(consent);
    this.forceShow.set(false);

    if (emitChange) {
      this.consentChanged$.next(consent);
    }
  }

  public giveAnalyticsConsent() {
    const consent: CookieConsent = {
      analytics: true,
      marketing: false,
      timestamp: Date.now(),
      version: COOKIE_CONSENT_VERSION,
    };

    this.save(consent);

    this.consentApi
      .submitConsent('ANALYTICS_COOKIES', '1')
      .pipe(
        tap(() => {
          this.workspaceStore.refreshUser();
        }),
        catchError((err) => {
          console.error('Failed to submit analytics consent:', err);
          return of(null);
        }),
      )
      .subscribe();
  }

  public rejectAnalyticsConsent() {
    const consent: CookieConsent = {
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
      version: COOKIE_CONSENT_VERSION,
    };

    this.save(consent);

    this.consentApi
      .submitConsent('ANALYTICS_COOKIES', '0')
      .pipe(
        tap(() => {
          this.workspaceStore.refreshUser();
        }),
        catchError((err) => {
          console.error('Failed to submit analytics rejection:', err);
          return of(null);
        }),
      )
      .subscribe();
  }

  public openConsentManager() {
    this.forceShow.set(true);
  }

  public hasConsent() {
    return this.consent() !== null;
  }

  public analyticsAllowed() {
    return this.consent()?.analytics ?? false;
  }

  /**
   * Restores local consent from user profile (logged-in users who already chose on another device).
   */
  public syncFromUserConsents(userConsents: Record<string, string> | undefined): void {
    const value = userConsents?.['ANALYTICS_COOKIES'];
    if (value !== '1' && value !== '0') {
      return;
    }

    const consent: CookieConsent = {
      analytics: value === '1',
      marketing: false,
      timestamp: Date.now(),
      version: COOKIE_CONSENT_VERSION,
    };

    const current = this.consent();
    if (
      current?.analytics === consent.analytics &&
      current?.version === consent.version
    ) {
      return;
    }

    this.save(consent);
  }

  public shouldShowBannerForGuest(): boolean {
    if (this.hasConsent()) {
      return false;
    }

    return true;
  }

  public shouldShowBanner(userConsents: Record<string, string> | undefined): boolean {
    const profileValue = userConsents?.['ANALYTICS_COOKIES'];
    if (profileValue === '1' || profileValue === '0') {
      return false;
    }

    if (this.hasConsent()) {
      return false;
    }

    if (this.forceShow()) {
      return true;
    }

    return true;
  }
}
